<script setup lang="ts">
import {useIntersectionObserver, useMediaControls} from '@vueuse/core';
import {IconMuted, IconPlay, IconUnmuted} from "~/components/ui/icons";
import {computed, onBeforeUnmount, onMounted, ref, watch} from 'vue';
import Video from "~/components/ui/video/Video.vue";

const props = defineProps<{
  src: string,
  muteButtonUp?: boolean,
  modalVideo?: boolean,
  postId: number;
  blur?: boolean,
}>();

const {sendEvent} = useEventLogger();

const handlePlay = () => sendEvent("video_view", props.postId);
const handleEnded = () => sendEvent("video_complete", props.postId);
const handleSkip = () => sendEvent("post_skip", props.postId);

const videoStore = useVideoStore();
const video = ref<HTMLVideoElement | null>(null);
const progress = ref(0);
const isVisible = ref(false);

const {
  duration, muted, playing,
  buffered,
  currentTime,
} = useMediaControls(video);

const toggleMute = (event: Event) => {
  event.stopPropagation();
  videoStore.toggleMuteAll();
  console.log('[VIDEO] toggleMute called. New muted state will be:', !videoStore.isMuted);
};

const togglePlayPause = (event: Event) => {
  event.stopPropagation();
  if (playing.value) {
    video.value?.pause();
  } else {
    video.value?.play();
  }
};

const updateProgress = () => {
  if (duration.value) {
    progress.value = (currentTime.value / duration.value) * 100;
  }
};

const endBuffer = computed(() => buffered.value.length > 0 ? buffered.value[buffered.value.length - 1][1] : 0)

const checkVisibility = () => {
  if (isVisible.value) {
    video.value?.play();
  } else {
    video.value?.pause();
  }
};

const {stop: stopObserver} = useIntersectionObserver(
  video,
  ([{isIntersecting, intersectionRatio}]) => {
    isVisible.value = isIntersecting && intersectionRatio >= 0.55;
    checkVisibility();
  },
  {
    threshold: [0, 0.55],
  }
);

watch(isVisible, checkVisibility);

watch(() => videoStore.isMuted, (newVal) => {
  if (video.value) {
    video.value.muted = newVal;
  }
});

onMounted(() => {
  if (video.value) {
    videoStore.registerVideo(video.value);
    muted.value = videoStore.isMuted;
    video.value.addEventListener('timeupdate', updateProgress);
    video.value.addEventListener('loadedmetadata', updateProgress);
  }
})


onBeforeUnmount(() => {
  if (video.value) {
    videoStore.unregisterVideo(video.value);
    video.value.removeEventListener('timeupdate', updateProgress);
    video.value.removeEventListener('loadedmetadata', updateProgress);
  }
  stopObserver();
});
</script>


<template>
  <div class="relative h-full w-full max-w-full">
    <div :class="['absolute right-3 z-[70]', muteButtonUp ? 'top-3' : 'top-18 md:top-3']">
      <button @click.stop="toggleMute"
              class="text-white w-10 h-10 flex justify-center items-center rounded-full p-2 bg-gray-dark backdrop-blur-lg">
        <IconMuted size="15px" v-if="muted"/>
        <IconUnmuted size="15px" v-else/>
      </button>
    </div>
    <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50">
      <button @click.stop="togglePlayPause"
              class="text-white rounded-full p-3">
        <IconPlay v-if="!playing" size="50px"/>
      </button>
    </div>
    <!--    <div class="absolute bottom-0 px-0 flex items-center g-0.5 w-full h-px z-20">-->
    <!--      <Scrubber v-model="currentTime" :max="duration" :secondary="endBuffer" class="w-full" @click.stop @mousedown.stop-->
    <!--                @mouseup.stop @touchstart.stop @touchend.stop>-->
    <!--      </Scrubber>-->
    <!--    </div>-->
    <video ref="video"
           playsinline x5-playsinline="true" webkit-playsinline="true"
           preload="auto"
           :muted="videoStore.isMuted"
           controlsList="nodownload nofullscreen"
           loop
           poster
           class="w-full h-full object-cover md:rounded-2xl"
           :class="{ 'blur-md': blur }"
           @play="handlePlay"
           @ended="handleEnded"
           @seeked="handleSkip"
           @click.stop="togglePlayPause"
           :src="src">
    </video>
  </div>
</template>
<style>
video {
  z-index: 10;
  position: relative;
}
</style>
